import {
  CloudSyncIcon,
  CloudUploadIcon,
  DownloadIcon,
  FileUpIcon,
  FolderUpIcon,
  FunnelIcon,
  PlusIcon,
  RefreshCwIcon,
  RotateCcwIcon,
  SearchIcon,
  TextSearchIcon,
  type LucideIcon,
} from 'lucide-react';
import type { VariantProps } from 'class-variance-authority';
import type { buttonVariants } from './variants';

/** variant 取值范围直接由 buttonVariants 推导，避免预设与样式定义两处维护 */
export type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;

/** 单个图标预设：图标组件 + 未显式传入 variant 时采用的默认值 */
export interface ButtonIconPresetConfig {
  icon: LucideIcon;
  variant: ButtonVariant;
}

/**
 * 表格操作区常用按钮的图标预设
 *
 * key 即 `<Button icon="xxx" />` 的可选值。`variant` 只是**默认值**，
 * 调用方显式传入 `variant` 时以显式值为准；图标统一以 `w-3.5 h-3.5` 渲染在 children 左侧。
 */
export const BUTTON_ICON_PRESETS = {
  /** 重置 */
  reset: { icon: RotateCcwIcon, variant: 'outline' },
  /** 搜索 */
  search: { icon: TextSearchIcon, variant: 'default' },
  /** 查找 */
  find: { icon: SearchIcon, variant: 'outline' },
  /** 筛选 */
  filter: { icon: FunnelIcon, variant: 'outline' },
  /** 导出 */
  export: { icon: DownloadIcon, variant: 'outline' },
  /** 刷新 */
  refresh: { icon: RefreshCwIcon, variant: 'outline' },
  /** 同步 */
  sync: { icon: CloudSyncIcon, variant: 'outline' },
  /** 推送 */
  push: { icon: CloudUploadIcon, variant: 'outline' },
  /** 新增 */
  plus: { icon: PlusIcon, variant: 'default' },
  /** 文件上传 */
  fileUpload: { icon: FileUpIcon, variant: 'default' },
  /** 文件夹上传 */
  folderUpload: { icon: FolderUpIcon, variant: 'default' },
} as const satisfies Record<string, ButtonIconPresetConfig>;

/** `<Button icon="..." />` 的图标预设枚举 */
export type ButtonIconPreset = keyof typeof BUTTON_ICON_PRESETS;

/** 判断字符串是否命中内置图标预设，避免自定义字符串被误当成预设 */
export const isButtonIconPreset = (value: string): value is ButtonIconPreset =>
  Object.prototype.hasOwnProperty.call(BUTTON_ICON_PRESETS, value);
